'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useParams, useLocation, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import NavMarker from 'react-rhumb/NavMarker';
import { useQuery } from 'data-fetching-client';
import { parse as parseParams } from 'hub-http/helpers/params';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import Loadable from 'UIComponents/decorators/Loadable';
import HR from 'UIComponents/elements/HR';
import Small from 'UIComponents/elements/Small';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UILink from 'UIComponents/link/UILink';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UISection from 'UIComponents/section/UISection';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import { useWindowDimensionsContext } from 'marketplace-ui-common/hooks/useWindowDimensionsContext';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { resetDetailsReferrer } from '../../actions/DetailsActions';
import * as FilterKey from '../../constants/search/FilterKey';
import { NAV_BAR_HEIGHT_INT } from '../../constants';
import StorefrontFilters from '../../records/StorefrontFilters';
import { getHasZorseChatScope, getHasZorseWebticketScope, getIsCustomer, getIsPublic, PublicOnly } from '../../selectors/auth';
import { getListingViewReferrer, makeGetListingView } from '../../selectors/details';
import { getFullHref } from '../../utils/history';
import { useParsedPathParams } from '../../utils/hooks/useParsedPathParams';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
import { useHeaderTabs } from 'marketplace-ui-common/hooks/useHeaderTabs';
import { useScrollEventListener } from 'ecosystem-marketplace-core/hooks/useScrollEventListener';
import { useScrollToTop } from 'ecosystem-marketplace-core/hooks/useScrollToTop';
import { getAllReviewsUrl, getFormattedUrl, getListingDetailUrl } from '../../utils/urls';
import { getUsageTracker } from '../../utils/usageTracker';
import { openHelpWidget } from '../../utils/zorse';
import CanonicalLink from '../elements/CanonicalLink';
import { DetailMainSection as MainSection } from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MainSectionHeading from 'ecosystem-marketplace-core/components/layout/MainSectionHeading';
import InnerSection from 'ecosystem-marketplace-core/components/layout/InnerSection';
import InnerSectionHeading from 'ecosystem-marketplace-core/components/layout/InnerSectionHeading';
import AppInstallability from 'ecosystem-marketplace-core/records/AppInstallability';
import Header, { TAB_IDS } from './Header';
import Carousel from './overview/Carousel';
import Benefits from './overview/Benefits';
import IntegrationFeatures from './overview/IntegrationFeatures';
import SharedData from './overview/SharedData';
import HubSpotFeatures from './overview/HubSpotFeatures';
import AppIntegrations from './overview/AppIntegrations';
import AppDetails from './overview/sidebar/AppDetails';
import Requirements from './overview/sidebar/Requirements';
import Resources from './overview/sidebar/Resources';
import Contact from './overview/sidebar/Contact';
import Privacy from './overview/sidebar/Privacy';
import Pricings from './pricing/Pricings';
import Reviews from './reviews/Reviews';
import FeatureCompatibilityModal from './compatibility/FeatureCompatibilityModal';
import MoreApps from './MoreApps';
import { fetchFeatureCompatibility } from '../../actions/DetailsActions';
import { getFeatureCompatibilityRequestState } from '../../selectors/requestStates';
import { isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import { GET_INSTALLABILITY } from '../../queries/installability';
const {
  CATEGORY,
  SUPER_CATEGORY
} = FilterKey;
const AsyncCtaBanner = Loadable({
  loader: () => import(
  /* webpackChunkName: 'detail-cta-banner' */
  './CtaBanner')
});

const SidebarSection = props => /*#__PURE__*/_jsx(InnerSection, Object.assign({}, props, {
  compact: true
}));

const Section = ({
  children,
  label
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});

const DetailPage = ({
  isAdminPreview
}) => {
  const reduxDispatch = useDispatch();
  const {
    pathname,
    search
  } = useLocation();
  const params = useParams();
  const {
    path,
    url
  } = useRouteMatch();
  const {
    height: windowHeight
  } = useWindowDimensionsContext();
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  useScrollToTop(pathname);
  const {
    category: activeCategory,
    superCategory: activeSuperCategory,
    slug
  } = useParsedPathParams(params);
  const getListingView = useMemo(makeGetListingView, []);
  const hasZorseChatScope = useSelector(getHasZorseChatScope);
  const hasZorseWebticketScope = useSelector(getHasZorseWebticketScope);
  const isCustomer = useSelector(getIsCustomer);
  const referrer = useSelector(getListingViewReferrer);
  const listingView = useSelector(state => getListingView(state, {
    slug
  }));
  const {
    published,
    source,
    language: languageQuery
  } = parseParams(search.slice(1));
  const langCode = getLang();
  const isPreview = published === 'false' || path === '/_preview/:slug' || isAdminPreview;
  const isPublic = useSelector(getIsPublic);
  const isReviewLink = path.includes('/:slug/write-review');
  const tracker = getUsageTracker();
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const listingCategory = useMemo(() => {
    return activeCategory || listingView.category || null;
  }, [activeCategory, listingView]);
  const listingSuperCategory = useMemo(() => {
    return activeSuperCategory || listingView.getSuperCategory() || null;
  }, [activeSuperCategory, listingView]);
  const scrollOffset = NAV_BAR_HEIGHT_INT + windowHeight / 2;
  const {
    calculateActiveTab,
    scrollToTabSection,
    activeTab
  } = useHeaderTabs(Object.values(TAB_IDS), scrollOffset);
  useScrollEventListener(calculateActiveTab);
  const {
    agencyPricingUrl,
    appIntegrations,
    appLanguages,
    appUrl,
    benefits,
    casesUrl,
    categoryFeatures,
    chatUrl,
    companyName,
    companyUrl,
    currency,
    dataSyncHowItWorks,
    dataSyncInfo,
    dataSyncs,
    email,
    facebookUrl,
    forumUrl,
    hubSpotFeatures,
    iconUrl,
    id,
    integrationFeatures,
    keywords,
    name,
    neededScopeGroups,
    offering: {
      certifiedAt,
      connectionType,
      homemade,
      installsCount
    },
    offeringId,
    opsHub,
    phone,
    pricingUrl,
    pricings,
    privacy2Url,
    privacy3Url,
    privacyUrl,
    reviewability,
    screenshots,
    setupUrl,
    supportLanguages,
    supportUrl,
    tagline,
    termsUrl,
    tiers,
    updatedAt,
    videoUrl
  } = listingView;
  const appId = listingView.getAppId();
  const detailsClassName = isMobileView ? 'p-top-6' : "";
  const overviewClassName = !isMobileView ? 'p-right-15' : "";
  const [featureCompatibilityModalOpen, setFeatureCompatibilityModalOpen] = useState(false);
  const featureCompatibilityRequestStatus = useSelector(getFeatureCompatibilityRequestState);
  const dispatch = useDispatch();
  useEffect(() => {
    if (opsHub && isUninitialized(featureCompatibilityRequestStatus)) {
      dispatch(fetchFeatureCompatibility());
    }
  }, [opsHub, featureCompatibilityRequestStatus, dispatch]);
  const installabilityQuery = useQuery(GET_INSTALLABILITY, {
    variables: {
      offeringId
    },
    skip: !isCustomer
  });
  const installability = !isCustomer || !installabilityQuery.data ? new AppInstallability() : installabilityQuery.data.installability;
  const documentTitle = useMemo(() => {
    return unescapedText('appMarketplace.detail.htmlTitle', {
      name
    });
  }, [name]);
  const preferredLanguage = useMemo(() => {
    return isPreview ? languageQuery || 'en' : langCode;
  }, [langCode, languageQuery, isPreview]);
  const eventProperties = useMemo(() => {
    let screen;

    if (isPreview) {
      screen = 'detail preview';
    } else if (isReviewLink) {
      screen = 'detail review';
    } else {
      screen = 'detail';
    }

    return Object.assign({}, listingView.getEventProperties(), {
      slug,
      screen,
      preferredLanguage
    });
  }, [isPreview, isReviewLink, listingView, slug, preferredLanguage]);
  const trackEvent = useCallback((eventName, properties = {}) => {
    tracker.track(eventName, Object.assign({}, properties, {}, eventProperties));
  }, [eventProperties, tracker]);
  const trackConnectButtonClick = useCallback(() => {
    trackEvent('listing-detail-click-connect');
  }, [trackEvent]);
  const trackInteraction = useCallback((action, section, value, properties = {}) => {
    trackEvent('listing-detail-interaction', Object.assign({
      action,
      section,
      value
    }, properties));
  }, [trackEvent]);
  const trackPageview = useCallback((properties = {}) => {
    let eventName;

    if (isPreview) {
      eventName = 'listing-detail-preview-pageview';
    } else if (isReviewLink) {
      eventName = 'listing-detail-review-pageview';
    } else {
      eventName = 'listing-detail-pageview';
    }

    trackEvent(eventName, Object.assign({}, properties, {
      referrer: referrer || undefined,
      preferredLanguage,
      source
    }));
  }, [isPreview, isReviewLink, referrer, source, trackEvent, preferredLanguage]);
  const pricingLastUpdated = useMemo(() => {
    if (pricings.first() && pricings.first().updatedAt) {
      return pricings.first().updatedAt;
    }

    return updatedAt;
  }, [pricings, updatedAt]);
  const getFilterLink = useCallback((filterKey, tagName, isAllCategories = false) => {
    let filters = StorefrontFilters.from(Object.assign({}, filterKey && {
      [filterKey]: [tagName]
    }, {
      [CATEGORY]: listingCategory,
      [SUPER_CATEGORY]: listingSuperCategory
    }));

    if (isAllCategories) {
      filters = filters.toAllCategories();
    }

    return filters.toLocation();
  }, [listingCategory, listingSuperCategory]);
  useEffect(() => {
    document.title = documentTitle;

    if (!pageViewTracked) {
      trackPageview();
      setPageViewTracked(true);
    }
  }, [documentTitle, pageViewTracked, trackPageview]);
  useEffect(() => {
    if (referrer) {
      reduxDispatch(resetDetailsReferrer());
    }
  }, [reduxDispatch, referrer]);
  const renderedResourcesSection = useMemo(() => /*#__PURE__*/_jsxs(SidebarSection, {
    children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.resources.headingText"
      })
    }), /*#__PURE__*/_jsx(Resources, {
      casesUrl: casesUrl,
      forumUrl: forumUrl,
      onTrackInteraction: trackInteraction,
      setupUrl: setupUrl,
      supportUrl: supportUrl
    })]
  }), [casesUrl, forumUrl, trackInteraction, setupUrl, supportUrl]);
  const renderedSupportLanguagesList = useMemo(() => /*#__PURE__*/_jsx(FormattedList, {
    children: supportLanguages.map(languageCode => [languageCode, `SharedI18nStrings.languageNames.${languageCode}`]).sortBy(([__, languageNameI18nKey]) => unescapedText(languageNameI18nKey)).map(([languageCode, languageNameI18nKey]) => /*#__PURE__*/_jsx(FormattedMessage, {
      message: languageNameI18nKey
    }, languageCode)).toArray()
  }), [supportLanguages]);
  const renderedSupportSection = useMemo(() => /*#__PURE__*/_jsxs(SidebarSection, {
    children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
      children: homemade ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.support.homemadeHeadingText",
        options: {
          name
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.support.headingText",
        options: {
          companyName
        }
      })
    }), /*#__PURE__*/_jsx(Contact, {
      chatUrl: chatUrl,
      companyUrl: companyUrl,
      email: email,
      facebookUrl: facebookUrl,
      hasZorseChatScope: hasZorseChatScope,
      hasZorseWebticketScope: hasZorseWebticketScope,
      homemade: homemade,
      isCustomer: isCustomer,
      onTrackInteraction: trackInteraction,
      openHelpWidget: openHelpWidget,
      phone: phone
    }), /*#__PURE__*/_jsx(UIDescriptionList, {
      className: "p-top-2",
      children: /*#__PURE__*/_jsx(Section, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.detail.overview.sidebar.support.supportedLanguages"
        }),
        children: renderedSupportLanguagesList
      })
    })]
  }), [chatUrl, companyName, companyUrl, email, facebookUrl, hasZorseChatScope, hasZorseWebticketScope, homemade, isCustomer, renderedSupportLanguagesList, trackInteraction, phone, name]);
  const renderedPrivacySection = useMemo(() => /*#__PURE__*/_jsxs(SidebarSection, {
    children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.overview.sidebar.privacy.headingText"
      })
    }), /*#__PURE__*/_jsx(Privacy, {
      homemade: homemade,
      onTrackInteraction: trackInteraction,
      privacyUrl: privacyUrl,
      privacy2Url: privacy2Url,
      privacy3Url: privacy3Url,
      termsUrl: termsUrl
    })]
  }), [homemade, trackInteraction, privacyUrl, privacy2Url, privacy3Url, termsUrl]);
  const renderedReportLink = useMemo(() => {
    return isCustomer && hasZorseWebticketScope && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(HR, {
        distance: "medium"
      }), /*#__PURE__*/_jsx(SidebarSection, {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "appMarketplace.detail.overview.sidebar.reportApp_jsx",
          options: {
            onClick: () => {
              trackInteraction('click report this app link', 'report');
              openHelpWidget({
                create: true
              });
            }
          },
          elements: {
            Link: UILink
          }
        })
      })]
    });
  }, [isCustomer, hasZorseWebticketScope, trackInteraction]);
  const socialImageUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [isPreview && !isAdminPreview && /*#__PURE__*/_jsx(UIAlert, {
      titleText: unescapedText('appMarketplace.detail.previewAlert.titleText'),
      type: "warning",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "appMarketplace.detail.previewAlert.message"
      })
    }), /*#__PURE__*/_jsxs(UIListingPage, {
      contentAreaFlush: true,
      headerComponent: /*#__PURE__*/_jsx(NavMarker, {
        name: "DETAIL_HEADER_LOAD",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(Header, {
            activeTab: activeTab,
            appId: appId,
            appName: name,
            appUrl: appUrl,
            category: listingCategory,
            certifiedAt: certifiedAt,
            connectionType: connectionType,
            iconUrl: iconUrl,
            installability: installability,
            isCustomer: isCustomer,
            isPreview: isPreview,
            isDataSyncApp: opsHub,
            listingId: id,
            onConnectButtonClick: trackConnectButtonClick,
            onTrackEvent: trackEvent,
            onTrackInteraction: trackInteraction,
            onTrackPageview: trackPageview,
            scrollToTabSection: scrollToTabSection,
            setupUrl: setupUrl,
            superCategory: listingSuperCategory,
            tagline: tagline
          })
        })
      }),
      mainSectionBackgroundColor: OLAF,
      pageBackgroundColor: GYPSUM,
      pageLayout: "full-width",
      children: [/*#__PURE__*/_jsx(MainSection, {
        id: "overview",
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsxs(UIGrid, {
            children: [/*#__PURE__*/_jsxs(UIGridItem, {
              className: overviewClassName,
              size: {
                xs: 12,
                md: 8
              },
              children: [/*#__PURE__*/_jsx(InnerSection, {
                children: /*#__PURE__*/_jsx(Carousel, {
                  onTrackInteraction: trackInteraction,
                  screenshots: screenshots,
                  videoUrl: videoUrl
                })
              }), /*#__PURE__*/_jsxs(InnerSection, {
                children: [/*#__PURE__*/_jsx(MainSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(Benefits, {
                  benefits: benefits
                })]
              }), /*#__PURE__*/_jsxs(InnerSection, {
                id: "features",
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  className: "m-bottom-6",
                  itemRight: opsHub ? /*#__PURE__*/_jsx(UIButton, {
                    use: "link",
                    onClick: () => {
                      setFeatureCompatibilityModalOpen(true);
                      trackInteraction('click compare features by plan', 'overview');
                    },
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "appMarketplace.detail.overview.integrationFeatures.compatibilityLink"
                    })
                  }) : null,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.integrationFeatures.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(IntegrationFeatures, {
                  integrationFeatures: integrationFeatures,
                  isOpsHubListing: opsHub,
                  onTrackInteraction: trackInteraction
                })]
              }), /*#__PURE__*/_jsxs(InnerSection, {
                id: "sharedData",
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  className: "m-bottom-2",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.sharedData.headingText"
                  })
                }), /*#__PURE__*/_jsx(SharedData, {
                  dataSyncHowItWorks: dataSyncHowItWorks,
                  dataSyncInfo: dataSyncInfo,
                  dataSyncs: dataSyncs,
                  iconUrl: iconUrl,
                  name: name,
                  onTrackInteraction: trackInteraction,
                  opsHub: opsHub
                })]
              }), /*#__PURE__*/_jsxs(InnerSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.hubspotFeatures.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(HubSpotFeatures, {
                  hubspotFeatures: hubSpotFeatures,
                  getFilterLink: getFilterLink,
                  isPreview: isPreview
                })]
              }), !appIntegrations.isEmpty() && /*#__PURE__*/_jsxs(InnerSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.appIntegrations.headingText",
                    options: {
                      name
                    }
                  })
                }), /*#__PURE__*/_jsx(AppIntegrations, {
                  integrations: appIntegrations
                })]
              })]
            }), /*#__PURE__*/_jsxs(UIGridItem, {
              className: detailsClassName,
              size: {
                xs: 12,
                md: 4
              },
              children: [/*#__PURE__*/_jsxs(SidebarSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.sidebar.details.headingText"
                  })
                }), /*#__PURE__*/_jsx(AppDetails, {
                  appLanguages: appLanguages,
                  category: listingCategory,
                  categoryFeatures: categoryFeatures,
                  companyName: companyName,
                  companyUrl: companyUrl,
                  getFilterLink: getFilterLink,
                  homemade: homemade,
                  installs: installsCount,
                  isPreview: isPreview,
                  onTrackInteraction: trackInteraction
                })]
              }), /*#__PURE__*/_jsxs(SidebarSection, {
                children: [/*#__PURE__*/_jsx(InnerSectionHeading, {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "appMarketplace.detail.overview.sidebar.requirements.headingText"
                  })
                }), /*#__PURE__*/_jsx(Requirements, {
                  appName: name,
                  companyName: companyName,
                  connectionType: connectionType,
                  homemade: homemade,
                  iconUrl: iconUrl,
                  installability: installability,
                  integrationFeatures: integrationFeatures,
                  isCustomer: isCustomer,
                  isOpsHubListing: opsHub,
                  neededScopeGroups: neededScopeGroups,
                  onOpenFeatureCompatibilityModal: () => setFeatureCompatibilityModalOpen(true),
                  onTrackInteraction: trackInteraction,
                  pricingUrl: pricingUrl,
                  pricings: pricings,
                  scrollToTabSection: scrollToTabSection,
                  tiers: tiers
                })]
              }), !isMobileView && /*#__PURE__*/_jsxs(Fragment, {
                children: [renderedResourcesSection, renderedSupportSection, renderedPrivacySection, renderedReportLink]
              })]
            })]
          })
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        id: "pricing",
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [/*#__PURE__*/_jsx(MainSectionHeading, {
            itemRight: /*#__PURE__*/_jsxs(Fragment, {
              children: [agencyPricingUrl && /*#__PURE__*/_jsx(UIButton, {
                external: true,
                href: getFormattedUrl(agencyPricingUrl),
                onClick: () => trackInteraction('click agency pricing link', 'pricing'),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.pricing.agencyPricingLinkText"
                })
              }), pricingUrl && /*#__PURE__*/_jsx(UIButton, {
                external: true,
                href: getFormattedUrl(pricingUrl),
                onClick: () => trackInteraction('click software pricing link', 'pricing'),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "appMarketplace.detail.pricing.pricingLinkText"
                })
              })]
            }),
            subheading: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.pricing.subheadingText",
              options: {
                name
              }
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "appMarketplace.detail.pricing.headingText",
              options: {
                name
              }
            })
          }), /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Pricings, {
              currency: currency,
              onTrackInteraction: trackInteraction,
              pricings: pricings
            })
          }), /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(Small, {
              children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                message: "appMarketplace.detail.pricing.disclaimerText_jsx",
                elements: {
                  b: 'b',
                  Date: FormattedDateTime
                },
                options: {
                  format: 'L',
                  type: isCustomer ? 'portalTz' : 'utc',
                  value: pricingLastUpdated
                }
              })
            })
          })]
        })
      }), isMobileView && /*#__PURE__*/_jsx(MainSection, {
        color: "light",
        children: /*#__PURE__*/_jsxs(MainSectionInner, {
          children: [renderedResourcesSection, renderedSupportSection, renderedPrivacySection, renderedReportLink]
        })
      }), /*#__PURE__*/_jsx(MainSection, {
        children: /*#__PURE__*/_jsx(MainSectionInner, {
          id: "reviews",
          children: /*#__PURE__*/_jsx(UISection, {
            children: /*#__PURE__*/_jsx(Reviews, {
              allReviewsHref: getAllReviewsUrl(isPublic, slug, listingCategory, listingSuperCategory),
              appName: name,
              appIcon: iconUrl,
              isPreview: isPreview,
              isPublic: isPublic,
              isReviewLink: isReviewLink,
              listingDetailUrl: getListingDetailUrl(slug, listingCategory, listingSuperCategory),
              offeringId: offeringId,
              reviewability: reviewability,
              slug: slug,
              supportLink: companyUrl,
              trackInteraction: trackInteraction
            })
          })
        })
      }), !isCustomer && /*#__PURE__*/_jsx(MainSection, {
        color: "dark",
        children: /*#__PURE__*/_jsx(InnerSection, {
          children: /*#__PURE__*/_jsx(AsyncCtaBanner, {
            appName: name,
            appLink: pricingUrl,
            companyName: companyName,
            companyUrl: companyUrl,
            homemade: homemade,
            pathname: pathname,
            onTrackInteraction: trackInteraction
          })
        })
      }), /*#__PURE__*/_jsx(MoreApps, {
        category: listingCategory,
        superCategory: listingSuperCategory,
        slug: slug,
        trackInteraction: trackInteraction
      }), featureCompatibilityModalOpen && /*#__PURE__*/_jsx(FeatureCompatibilityModal, {
        integrationFeatures: integrationFeatures,
        onClose: () => setFeatureCompatibilityModalOpen(false),
        trackInteraction: trackInteraction
      })]
    }), /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(CanonicalLink, {
          to: url
        }), /*#__PURE__*/_jsx("meta", {
          name: "description",
          content: tagline
        }), /*#__PURE__*/_jsx("meta", {
          name: "keywords",
          content: keywords.join(',')
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: documentTitle
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: getFullHref(url)
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: tagline
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {})]
      })
    })]
  });
};

DetailPage.propTypes = {
  isAdminPreview: PropTypes.bool.isRequired
};
DetailPage.defaultProps = {
  isAdminPreview: false
};
export default DetailPage;